/**
 * The web's role vocabulary: one union, two label maps, and the rule that turns
 * `GET /users/me`'s `assignableRoles` into the options the role picker renders
 * (P5 design §E).
 *
 * WHY A MODULE AND NOT FIVE MenuItems: the labels were inline in
 * `pages/Settings/UserManagement.tsx`'s Select and inline AGAIN in `App.tsx`'s
 * `getRoleLabel`, and the union was typed out a third time in
 * `onboarding/tours.ts`. Three copies means a new role gets added to two of them.
 * Both maps here are `Record<UserRoleSlug, string>`, so a role with no label is a
 * COMPILE error — the web's half of the totality proof INV-103 makes on the
 * backend, where `Record<UserRole, …>` does the same for `ROLE_DEFAULTS`.
 *
 * ⚠ NOTHING IS IMPORTED AS A VALUE. The union arrives through an `import type`,
 * which is erased, so this module has no runtime dependency at all — that is what
 * lets `onboarding/tours.ts` share the union without dragging Firebase, Dexie and
 * the axios instance into the onboarding graph.
 */
import type { UserClaims } from './useRBAC';

/** The values of `enum UserRole` in `backend/prisma/schema.prisma`, welded to it
 *  by the drift guard in `roles.test.ts`. */
export type UserRoleSlug = UserClaims['role'];

/**
 * Full labels — the role picker, and anywhere a role is named in prose.
 * ⚠ KEY ORDER IS LOAD-BEARING: `ROLE_PICKER_ORDER` is derived from it, so this is
 * the order the Select renders in. It is `enum UserRole`'s DECLARATION order —
 * rank-descending with the two rank-1 roles (VETERINARIAN, VETERINARY_TECHNICIAN)
 * in declaration order, CSR last. Rank is not a total order and cannot be the
 * authority: those two share a rank, so only the enum decides which comes first,
 * and `roles.test.ts` welds this list to `schema.prisma` for exactly that reason.
 */
export const ROLE_LABELS: Record<UserRoleSlug, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  VETERINARIAN: 'Veterinarian',
  VETERINARY_TECHNICIAN: 'Veterinary Technician',
  // The front desk (P5 §E). Spelled out in full here because this is the label
  // on the control that ASSIGNS the role — "CSR" alone is the short form.
  CSR: 'Customer Service Rep',
};

/** Short labels for chrome with no room for the full one (the sidebar identity
 *  block). Same keys, different words — not a derivation of the map above. */
export const ROLE_SHORT_LABELS: Record<UserRoleSlug, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  VETERINARIAN: 'Veterinarian',
  VETERINARY_TECHNICIAN: 'Vet Tech',
  CSR: 'CSR',
};

/** DERIVED, never retyped: a literal list could silently omit a role the maps
 *  already have, which is the exact drift this module exists to end. */
export const ROLE_PICKER_ORDER = Object.keys(ROLE_LABELS) as UserRoleSlug[];

/**
 * What the picker offers when `GET /users/me` did not ANSWER with an
 * `assignableRoles` array: the four roles `UserManagement` hardcoded before P5.
 *
 * ⚠ CSR is deliberately absent, and stays absent now that the role is assignable
 * by default (2026-09-21, #65). The reason changed with the flip and is worth
 * stating precisely: it is no longer "none of these can be a deployment where CSR
 * is assignable" — a stale cached payload from today's backend obviously can be —
 * it is that a FALLBACK fires exactly when the screen does not know the answer,
 * and the honest response to that is the NARROW list plus an API refusal, never a
 * guess that offers a role. Falling back is also never WIDER than the pre-P5
 * screen, which offered all four to everyone and let the API refuse.
 */
export const LEGACY_ASSIGNABLE_ROLES: readonly UserRoleSlug[] = [
  'OWNER',
  'ADMIN',
  'VETERINARIAN',
  'VETERINARY_TECHNICIAN',
];

/**
 * The options for the role picker, given whatever `GET /users/me` handed back.
 *
 * ABSENT IS NOT EMPTY — the same rule `mePermissions.ts` applies to the
 * permission array, for the same reason. A non-array, an EMPTY array, or an array
 * naming no role THIS build has a label for, is a NON-ANSWER and falls back; an
 * answer is honoured exactly and re-ordered into `ROLE_PICKER_ORDER`, so the
 * server's ordering never reorders the UI.
 *
 * ⚠ `[]` FALLING BACK is a deliberate call, and it is the one a reader will
 * question. The shipped rule cannot produce `[]` for anyone who can reach this
 * screen: the screen needs `manage:users`, only OWNER/ADMIN hold it by default,
 * and `assignableRolesFor` answers `[]` only for a caller whose role is not in
 * the enum at all. So an empty array here means the payload is WRONG (a truncated
 * cache, a replica row, a proxy that stripped the key), not that the API has
 * decided this user may assign nothing — and the honest response to a payload we
 * do not believe is the pre-P5 behaviour, which offered all four and let the API
 * refuse. Rendering an empty picker would instead read as "this screen is broken"
 * while withholding CSR, which the fallback also does. If a future rule really
 * means "assign nothing", it should say so with an explicit field, not with `[]`.
 *
 * A role this build cannot name is dropped rather than rendered raw: a control
 * that WRITES a role must not offer a value it cannot describe, and an older web
 * build refusing to offer a newer role is the conservative direction (the API
 * decides what is assignable either way).
 */
export function resolveAssignableRoles(raw: unknown): UserRoleSlug[] {
  // A COPY on both fallback arms, never the shared constant: this returns a mutable
  // `UserRoleSlug[]` that callers are free to sort or splice, and handing them the module's own
  // array would let one screen's edit reach every other caller of the fallback. The constant is
  // `readonly` so that mistake is a compile error rather than a bug found in the picker.
  if (!Array.isArray(raw)) return [...LEGACY_ASSIGNABLE_ROLES];
  const offered = new Set(raw.filter((role): role is string => typeof role === 'string'));
  const known = ROLE_PICKER_ORDER.filter((role) => offered.has(role));
  return known.length > 0 ? known : [...LEGACY_ASSIGNABLE_ROLES];
}
